/**
 * PerformanceTab - Performance metrics and training parameters
 *
 * Contains:
 * - Threshold pace + calculator
 * - FTP (Functional Threshold Power)
 * - Weekly distance
 * - Runner category
 */

import { Box, MenuItem, TextField, Typography } from '@mui/material';
import type { RunnerCategory, UserProfile } from '@shared/types';
import React, { useState } from 'react';
import { ThresholdPaceCalculator } from '../../ThresholdPaceCalculator';

interface PerformanceTabProps {
  profile: UserProfile;
  onUpdate: (updates: Partial<UserProfile>, requiresConfirmation?: boolean) => void;
  pendingChanges: Set<string>;
}

const RUNNER_CATEGORIES: Array<{ value: RunnerCategory; label: string; description: string }> = [
  { value: 'beginner', label: 'Beginner', description: '<20 km/week' },
  { value: 'intermediate', label: 'Intermediate', description: '20-70 km/week' },
  { value: 'advanced', label: 'Advanced', description: '>70 km/week' },
];

// Utility functions for pace conversion
const formatPaceFromSeconds = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const parsePaceToSeconds = (paceString: string): number => {
  const parts = paceString.split(':');
  if (parts.length !== 2) return 240;
  const minutes = parseInt(parts[0], 10) || 0;
  const seconds = parseInt(parts[1], 10) || 0;
  return minutes * 60 + seconds;
};

const deriveRunnerCategoryFromWeeklyMileage = (weeklyMileage: number): RunnerCategory => {
  if (weeklyMileage < 20) return 'beginner';
  if (weeklyMileage <= 70) return 'intermediate';
  return 'advanced';
};

const estimateFTPFromThresholdPace = (thresholdPaceSec: number): number => {
  const speedKmh = 3600 / thresholdPaceSec;
  const estimatedFTP = Math.round(3.0 * Math.pow(speedKmh, 2));
  return Math.max(100, Math.min(400, estimatedFTP));
};

const PerformanceTab: React.FC<PerformanceTabProps> = ({ profile, onUpdate }) => {
  const [paceInput, setPaceInput] = useState<string>(
    formatPaceFromSeconds(profile.thresholdPaceSec)
  );
  const [paceError, setPaceError] = useState<string>('');
  const [ftpInput, setFtpInput] = useState<string>(profile.FTP?.toString() || '');
  const [ftpAutoEstimate, setFtpAutoEstimate] = useState<boolean>(!profile.FTP);

  const handlePaceChange = (value: string) => {
    setPaceInput(value);

    const paceRegex = /^\d{1,2}:\d{2}$/;
    if (paceRegex.test(value)) {
      const seconds = parsePaceToSeconds(value);

      if (seconds < 150) {
        setPaceError('Pace seems too fast. Please enter a realistic threshold pace.');
      } else if (seconds > 480) {
        setPaceError('Pace seems too slow. Please enter a realistic threshold pace.');
      } else {
        setPaceError('');

        // Update pace and auto-estimate FTP if enabled
        const updates: Partial<UserProfile> = { thresholdPaceSec: seconds };
        if (ftpAutoEstimate) {
          const estimatedFTP = estimateFTPFromThresholdPace(seconds);
          updates.FTP = estimatedFTP;
          setFtpInput(estimatedFTP.toString());
        }
        onUpdate(updates, true);
      }
    } else {
      setPaceError('Please enter pace in mm:ss format (e.g., 4:30)');
    }
  };

  const handlePaceFromCalculator = (thresholdPaceSeconds: number) => {
    const formatted = formatPaceFromSeconds(thresholdPaceSeconds);
    setPaceInput(formatted);
    setPaceError('');

    const updates: Partial<UserProfile> = { thresholdPaceSec: thresholdPaceSeconds };
    if (ftpAutoEstimate) {
      const estimatedFTP = estimateFTPFromThresholdPace(thresholdPaceSeconds);
      updates.FTP = estimatedFTP;
      setFtpInput(estimatedFTP.toString());
    }
    onUpdate(updates, true);
  };

  const handleFtpChange = (value: string) => {
    setFtpInput(value);
    setFtpAutoEstimate(false);

    const ftpValue = parseInt(value, 10);
    if (value === '' || isNaN(ftpValue)) {
      onUpdate({ FTP: undefined }, true);
    } else if (ftpValue >= 50 && ftpValue <= 500) {
      onUpdate({ FTP: ftpValue }, true);
    }
  };

  const handleWeeklyMileageChange = (value: number) => {
    const newCategory = deriveRunnerCategoryFromWeeklyMileage(value);
    onUpdate(
      {
        weeklyMileage: value,
        runnerCategory: newCategory,
      },
      true
    );
  };

  const handleRunnerCategoryChange = (category: RunnerCategory) => {
    onUpdate({ runnerCategory: category }, true);
  };

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          ⚡ Performance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your fitness metrics that determine training zones and intensities.
        </Typography>
      </Box>

      {/* Threshold Pace */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Threshold Pace (Running)
        </Typography>
        <Box display="flex" alignItems="flex-start" gap={1}>
          <TextField
            fullWidth
            value={paceInput}
            onChange={e => handlePaceChange(e.target.value)}
            error={!!paceError}
            helperText={paceError || 'Your 1-hour max sustainable pace (e.g., 4:30 min/km)'}
            placeholder="4:00"
            size="small"
            aria-label="Threshold pace input"
            InputProps={{
              endAdornment: (
                <Typography variant="caption" color="text.secondary">
                  min/km
                </Typography>
              ),
            }}
          />
          <ThresholdPaceCalculator onSave={handlePaceFromCalculator} />
        </Box>
      </Box>

      {/* FTP */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          FTP (Cycling)
        </Typography>
        <TextField
          fullWidth
          type="number"
          value={ftpInput}
          onChange={e => handleFtpChange(e.target.value)}
          disabled={ftpAutoEstimate}
          helperText={
            ftpAutoEstimate
              ? 'Auto-estimated from running pace. Enter a value to override.'
              : 'Functional Threshold Power - your 1-hour max sustainable power'
          }
          placeholder="250"
          size="small"
          aria-label="FTP input"
          InputProps={{
            endAdornment: (
              <Typography variant="caption" color="text.secondary">
                watts
              </Typography>
            ),
          }}
          inputProps={{ min: 50, max: 500, step: 5 }}
        />
      </Box>

      {/* Weekly Distance */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Weekly Distance
        </Typography>
        <TextField
          fullWidth
          type="number"
          value={profile.weeklyMileage}
          onChange={e => handleWeeklyMileageChange(parseFloat(e.target.value) || 0)}
          helperText="Target total distance per week (running + cycling equivalent)"
          size="small"
          aria-label="Weekly distance target in kilometers"
          InputProps={{
            endAdornment: (
              <Typography variant="caption" color="text.secondary">
                km
              </Typography>
            ),
          }}
          inputProps={{ min: 1, step: 5 }}
        />
      </Box>

      {/* Weekly TSS Target */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Weekly TSS Target
        </Typography>
        <TextField
          fullWidth
          type="number"
          value={profile.weeklyTSSTarget || ''}
          onChange={e => {
            const value = parseInt(e.target.value, 10);
            onUpdate({ weeklyTSSTarget: isNaN(value) ? undefined : value }, true);
          }}
          helperText="Target Training Stress Score per week (used by AI Coach for plan generation)"
          placeholder="e.g., 400"
          size="small"
          aria-label="Weekly TSS target"
          InputProps={{
            endAdornment: (
              <Typography variant="caption" color="text.secondary">
                TSS
              </Typography>
            ),
          }}
          inputProps={{ min: 50, max: 2000, step: 25 }}
        />
      </Box>

      {/* Runner Category */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Athlete Level
        </Typography>
        <TextField
          fullWidth
          select
          value={
            profile.runnerCategory ?? deriveRunnerCategoryFromWeeklyMileage(profile.weeklyMileage)
          }
          onChange={e => handleRunnerCategoryChange(e.target.value as RunnerCategory)}
          helperText="Affects minimum workout distances and progression rate"
          size="small"
          aria-label="Select athlete level"
        >
          {RUNNER_CATEGORIES.map(cat => (
            <MenuItem key={cat.value} value={cat.value}>
              {cat.label} ({cat.description})
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Help text */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          💡 Tip: Your threshold pace determines all your training zones. If you've done a recent
          race or time trial, use the calculator to estimate your threshold.
        </Typography>
      </Box>
    </Box>
  );
};

export default PerformanceTab;
